package org.zq.linkgame.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.material3.MaterialTheme
import org.zq.linkgame.ui.components.AppText
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import org.zq.linkgame.ui.components.*
import org.zq.linkgame.ui.model.BackgroundTheme
import org.zq.linkgame.ui.model.LevelCategory
import org.zq.linkgame.ui.model.LevelPack
import org.zq.linkgame.ui.model.LocalStrings
import org.zq.linkgame.ui.model.SampleData
import org.zq.linkgame.ui.theme.*

/**
 * Get localized category name
 */
@Composable
fun LevelCategory.localizedName(): String {
    val strings = LocalStrings.current
    return when (this) {
        LevelCategory.ANIMAL -> strings.categoryAnimal
        LevelCategory.FOOD -> strings.categoryFood
        LevelCategory.FAIRY_TALE -> strings.categoryFairyTale
    }
}

/**
 * Level selection screen
 * Contains: Navigation bar with category tabs, level pack cards grid, pagination
 */
@Composable
fun LevelSelectScreen(
    onBack: () -> Unit,
    onLevelPackSelected: (LevelPack) -> Unit,
    backgroundTheme: BackgroundTheme = BackgroundTheme.SKY,
    modifier: Modifier = Modifier
) {
    var selectedCategory by remember { mutableStateOf(LevelCategory.ANIMAL) }
    var currentPage by remember { mutableStateOf(1) }
    
    val levelPacks = remember(selectedCategory) {
        SampleData.getLevelPacksByCategory(selectedCategory)
    }
    
    val itemsPerPage = 4
    val totalPages = (levelPacks.size + itemsPerPage - 1) / itemsPerPage
    
    val currentPageItems = remember(levelPacks, currentPage) {
        val startIndex = (currentPage - 1) * itemsPerPage
        val endIndex = minOf(startIndex + itemsPerPage, levelPacks.size)
        if (startIndex < levelPacks.size) {
            levelPacks.subList(startIndex, endIndex)
        } else {
            emptyList()
        }
    }
    
    BoxWithConstraints(
        modifier = modifier
            .fillMaxSize()
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(
                        Color(backgroundTheme.topColor),
                        Color(backgroundTheme.bottomColor)
                    )
                )
            )
    ) {
        val isLandscape = maxWidth > maxHeight
        
        if (isLandscape) {
            val textColor = Color(backgroundTheme.textColor)
            
            // Landscape layout
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(8.dp)
            ) {
                // Left: Back button and categories (vertical)
                Column(
                    modifier = Modifier
                        .width(100.dp)
                        .fillMaxHeight(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.SpaceEvenly
                ) {
                    TriangleNavigationButton(
                        onClick = onBack,
                        isLeft = true,
                        size = 40.dp,
                        backgroundColor = textColor
                    )
                    
                    LevelCategory.entries.forEach { category ->
                        CompactCategoryTab(
                            text = category.localizedName(),
                            isSelected = selectedCategory == category,
                            onClick = { 
                                selectedCategory = category
                                currentPage = 1
                            },
                            backgroundTheme = backgroundTheme
                        )
                    }
                }
                
                // Center: Level pack grid
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight()
                ) {
                    LevelPackGrid(
                        levelPacks = currentPageItems,
                        onLevelPackSelected = onLevelPackSelected,
                        isLandscape = true,
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxWidth()
                    )
                    
                    // Pagination at bottom
                    if (totalPages > 1) {
                        CompactPaginationBar(
                            currentPage = currentPage,
                            totalPages = totalPages,
                            onPreviousPage = { if (currentPage > 1) currentPage-- },
                            onNextPage = { if (currentPage < totalPages) currentPage++ },
                            backgroundTheme = backgroundTheme,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            }
        } else {
            // Portrait layout
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 12.dp, vertical = 8.dp)
            ) {
                TopNavigationBar(
                    selectedCategory = selectedCategory,
                    onCategorySelected = { 
                        selectedCategory = it
                        currentPage = 1
                    },
                    onBack = onBack,
                    backgroundTheme = backgroundTheme,
                    modifier = Modifier.fillMaxWidth()
                )
                
                Spacer(modifier = Modifier.height(12.dp))
                
                LevelPackGrid(
                    levelPacks = currentPageItems,
                    onLevelPackSelected = onLevelPackSelected,
                    isLandscape = false,
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxWidth()
                )
                
                Spacer(modifier = Modifier.height(8.dp))
                
                if (totalPages > 1) {
                    PaginationBar(
                        currentPage = currentPage,
                        totalPages = totalPages,
                        onPreviousPage = { if (currentPage > 1) currentPage-- },
                        onNextPage = { if (currentPage < totalPages) currentPage++ },
                        backgroundTheme = backgroundTheme,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
                
                Spacer(modifier = Modifier.height(8.dp))
            }
        }
    }
}

/**
 * Top navigation bar with back button and category tabs
 */
@Composable
private fun TopNavigationBar(
    selectedCategory: LevelCategory,
    onCategorySelected: (LevelCategory) -> Unit,
    onBack: () -> Unit,
    backgroundTheme: BackgroundTheme,
    modifier: Modifier = Modifier
) {
    val textColor = Color(backgroundTheme.textColor)
    
    Row(
        modifier = modifier
            .padding(top = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Back Button
        TriangleNavigationButton(
            onClick = onBack,
            isLeft = true,
            size = 40.dp,
            backgroundColor = textColor
        )
        
        Spacer(modifier = Modifier.width(12.dp))
        
        // Category Tabs - use SpaceEvenly for better distribution
        Row(
            modifier = Modifier.weight(1f),
            horizontalArrangement = Arrangement.SpaceEvenly,
            verticalAlignment = Alignment.CenterVertically
        ) {
            LevelCategory.entries.forEach { category ->
                CategoryTabButton(
                    text = category.localizedName(),
                    isSelected = selectedCategory == category,
                    onClick = { onCategorySelected(category) },
                    textColor = textColor
                )
            }
        }
    }
}

/**
 * Grid of level pack cards
 */
@Composable
private fun LevelPackGrid(
    levelPacks: List<LevelPack>,
    onLevelPackSelected: (LevelPack) -> Unit,
    isLandscape: Boolean = false,
    modifier: Modifier = Modifier
) {
    LazyVerticalGrid(
        columns = GridCells.Adaptive(minSize = if (isLandscape) 200.dp else 300.dp),
        modifier = modifier,
        contentPadding = PaddingValues(8.dp),
        horizontalArrangement = Arrangement.spacedBy(if (isLandscape) 12.dp else 20.dp),
        verticalArrangement = Arrangement.spacedBy(if (isLandscape) 12.dp else 30.dp)
    ) {
        items(
            items = levelPacks,
            key = { it.id }
        ) { levelPack ->
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                LevelPackCard(
                    levelPack = levelPack,
                    onClick = { 
                        if (levelPack.isUnlocked) {
                            onLevelPackSelected(levelPack)
                        }
                    },
                    isCompact = isLandscape
                )
            }
        }
    }
}

/**
 * Bottom pagination bar with prev/next buttons and page indicator
 */
@Composable
private fun PaginationBar(
    currentPage: Int,
    totalPages: Int,
    onPreviousPage: () -> Unit,
    onNextPage: () -> Unit,
    backgroundTheme: BackgroundTheme,
    modifier: Modifier = Modifier
) {
    val textColor = Color(backgroundTheme.textColor)
    
    Row(
        modifier = modifier
            .padding(bottom = 16.dp),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Previous Button
        TriangleNavigationButton(
            onClick = onPreviousPage,
            isLeft = true,
            size = 60.dp,
            backgroundColor = if (currentPage > 1) textColor else textColor.copy(alpha = 0.3f)
        )
        
        Spacer(modifier = Modifier.width(40.dp))
        
        // Page Indicator
        AppText(
            text = "$currentPage/$totalPages",
            style = MaterialTheme.typography.labelLarge,
            color = textColor
        )
        
        Spacer(modifier = Modifier.width(40.dp))
        
        // Next Button
        TriangleNavigationButton(
            onClick = onNextPage,
            isLeft = false,
            size = 60.dp,
            backgroundColor = if (currentPage < totalPages) textColor else textColor.copy(alpha = 0.3f)
        )
    }
}

/**
 * Compact category tab for landscape mode
 */
@Composable
private fun CompactCategoryTab(
    text: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    backgroundTheme: BackgroundTheme,
    modifier: Modifier = Modifier
) {
    val textColor = Color(backgroundTheme.textColor)
    
    AppText(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = if (isSelected) textColor else textColor.copy(alpha = 0.6f),
        modifier = modifier
            .background(
                if (isSelected) textColor.copy(alpha = 0.1f) else Color.Transparent,
                shape = androidx.compose.foundation.shape.RoundedCornerShape(8.dp)
            )
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp)
    )
}

/**
 * Compact pagination bar for landscape mode
 */
@Composable
private fun CompactPaginationBar(
    currentPage: Int,
    totalPages: Int,
    onPreviousPage: () -> Unit,
    onNextPage: () -> Unit,
    backgroundTheme: BackgroundTheme,
    modifier: Modifier = Modifier
) {
    val textColor = Color(backgroundTheme.textColor)
    
    Row(
        modifier = modifier.padding(vertical = 8.dp),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        TriangleNavigationButton(
            onClick = onPreviousPage,
            isLeft = true,
            size = 36.dp,
            backgroundColor = if (currentPage > 1) textColor else textColor.copy(alpha = 0.3f)
        )
        
        Spacer(modifier = Modifier.width(24.dp))
        
        AppText(
            text = "$currentPage/$totalPages",
            style = MaterialTheme.typography.bodyMedium,
            color = textColor
        )
        
        Spacer(modifier = Modifier.width(24.dp))
        
        TriangleNavigationButton(
            onClick = onNextPage,
            isLeft = false,
            size = 36.dp,
            backgroundColor = if (currentPage < totalPages) textColor else textColor.copy(alpha = 0.3f)
        )
    }
}
